import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { runInAction } from "mobx";

import { usePlayground } from "../../playground-store";

import usePlaygroundSessionFromId from "@/queries/playground/usePlaygroundSessionFromId";
import { usePromptVersion } from "@/queries";
import { useAuth } from "@/context/auth-context";

export enum HydrationState {
  IDLE,
  SUCCESS,
  ERROR,
}

const useHydrateSession = () => {
  const playground = usePlayground();
  const hasHydrated = useRef(false);

  const { data, isError, error, isLoading } = usePlaygroundSessionFromId(
    playground.workspace_id,
    playground.instanceId,
    true,
  );

  const [searchParams] = useSearchParams();
  const prompt_id = searchParams.get("prompt_id");
  const prompt_version = searchParams.get("prompt_version");

  const userToken = useAuth()?.userToken ?? "";

  const { data: promptVersionData } = usePromptVersion(
    userToken,
    prompt_id !== null ? Number(prompt_id) : null,
    prompt_version !== null ? Number(prompt_version) : null,
  );

  useEffect(() => {
    if (promptVersionData && !playground.selectedTemplateVersion) {
      runInAction(() => playground.setSelectedTemplateVersion(promptVersionData));
    }
  }, [promptVersionData, playground]);

  useEffect(() => {
    if ((isError || (data && !data?.success)) && playground.isPendingSessionInitialization) {
      playground.setIsPendingSessionInitialization(false);
    }
  }, [isError, playground, error, data?.success, data]);

  useEffect(() => {
    if (data && playground.isPendingSessionInitialization && !hasHydrated.current) {
      hasHydrated.current = true;
      runInAction(() => playground.initSession(data.playground_session));
    }
  }, [data, playground]);

  useEffect(() => {
    hasHydrated.current = false;
  }, [playground.instanceId]);

  if (data && !isError && !isLoading && data.success) {
    return HydrationState.SUCCESS;
  }

  if (isError) {
    return HydrationState.ERROR;
  }

  return HydrationState.IDLE;
};

export default useHydrateSession;
